import { AppNavigationGuard } from '@sennder/shell-utilities'

import { INITIAL_ROUTE_PATH } from '@/router'
import {
  canAccessCharteringPages,
  canAccessShipperContactsPages,
  canAccessCustomerDataPages,
  canAccessTenderingPages,
  canAccessTransportAnalysis,
} from './user-access'
import { ENV, isProductionEnv } from '@/common/config'
import { featureFlag } from '@/services/featureFlagProvider'

/**
 * Router Navigation Guards
 *
 * NAMING CONVENTIONS
 *
 * Route guard name should be a verb that describes what the guard does.
 *
 * - check{Page}Access: Check if the user has access to the page
 * - redirect{Page}: Redirect the user to a different page based on some criteria
 * - restrict{Criteria}: Restrict access to the page based on some criteria
 */

/**
 * Redirect users to the Trip page.
 */
export const redirectToTrip: AppNavigationGuard = async (to) => {
  return `/trips/${to.params.orderGroupId}${to.params.letter}`
}

/**
 * Redirect users to the Customer Order page when the feature flag is enabled.
 * If the feature flag is off, continue to the original shipment route.
 */
export const redirectToCustomerOrder: AppNavigationGuard = async (to) => {
  if (featureFlag.getFeatureFlag('ENABLE_CUSTOMER-ORDER-URL')) {
    return `/customer-order/${to.params.id}`
  }

  return true
}

/**
 * Redirect users to the Shipment page when the feature flag is disabled.
 * If the feature flag is on, continue to the original customer order route.
 */
export const redirectToShipment: AppNavigationGuard = async (to) => {
  if (!featureFlag.getFeatureFlag('ENABLE_CUSTOMER-ORDER-URL')) {
    return `/shipment/${to.params.id}`
  }

  return true
}

/**
 * Enable this page only in non-prod envs
 */
export const restrictInProduction: AppNavigationGuard = async () => {
  if (isProductionEnv()) {
    return { path: INITIAL_ROUTE_PATH }
  }
  return true
}

/**
 * This function determines whether the module should be visible to the user
 * based on the organization type.
 * Consider permission screen for the users that are not part of the chartering office
 */
export const restrictForCharteringOfficeUsers: AppNavigationGuard =
  async () => {
    if (canAccessCharteringPages()) {
      return true
    }
    return { path: INITIAL_ROUTE_PATH }
  }

export const checkShipperContactsAccess: AppNavigationGuard = () => {
  if (canAccessShipperContactsPages()) {
    return true
  }

  return { path: INITIAL_ROUTE_PATH }
}

export const checkCustomerDataAccess: AppNavigationGuard = () => {
  if (canAccessCustomerDataPages()) {
    return true
  }

  return { path: INITIAL_ROUTE_PATH }
}

export const redirectToSingleViewAP: AppNavigationGuard = () => {
  return '/single-view/ap-view/ap-view_all'
}

export const redirectToDefaultPage: AppNavigationGuard = () => {
  return { path: INITIAL_ROUTE_PATH }
}

export const canAcessTransportAnalysis: AppNavigationGuard = () => {
  if (canAccessTransportAnalysis()) {
    return true
  }

  return { path: INITIAL_ROUTE_PATH }
}

export const checkTenderingAccess: AppNavigationGuard = () => {
  if (canAccessTenderingPages()) {
    return true
  }

  return { path: INITIAL_ROUTE_PATH }
}

export const restrictToDev: AppNavigationGuard = () => {
  if (ENV === 'dev') {
    return true
  }

  return { path: INITIAL_ROUTE_PATH }
}
